import { computed, toValue } from 'vue'
import { useSecureStore } from '@/stores/SecureStore'
import { useAllowedFeatures } from '@/composables/app/useAllowedFeatures'
import { useProcedure, useProcedureExecution, useProcedureTransaction } from '@/composables/modules/procedures'
import { useStripe, useSentry, useReCaptcha } from '@/vendors/integrations'
import { get as _get, set as _set } from 'lodash'

export function useProcedureSecurity ({ procedure, procedure_execution }) {
  const { getProcedureSecureToken } = useSecureStore()
  const { allowedRecaptcha } = useAllowedFeatures()
  const { slug, category, paymentProvider } = useProcedure({ procedure })
  const { paymentMethod } = useProcedureExecution({ procedure, procedure_execution })
  const { hasTransaction, paymentMethodInformations } = useProcedureTransaction({ procedure, procedure_execution })
  const { is_recaptcha_processing, executeReCaptcha } = useReCaptcha()
  const { is_stripe_processing, getElement, clearElements, createStripePaymentMethod } = useStripe()
  const sentry = useSentry()

  const { procedure: procedureSecure } = getProcedureSecureToken({ id: slug })

  const secureToken = computed(() => _get(procedureSecure.value, 'token'))
  const isThirdPartySecurityProcessing = computed(() => is_stripe_processing.value || is_recaptcha_processing.value)

  const isRecaptchaRequired = computed(() => {
    const actions = toValue(procedure).actions

    if (!actions.recaptcha) return false
    if (allowedRecaptcha) return true

    return category.value === 'donation' && (!actions.transaction || actions.transaction.is_optional)
  })

  const set3dSecureToken = () => new Promise((resolve, reject) => {
    if (!hasTransaction.value || !['stripe', 'stripe_standard_connect'].includes(paymentProvider.value) || paymentMethod.value !== 'three_d_secure') return resolve()

    createStripePaymentMethod({ type: 'card', card: getElement('cardNumber') })
      .then(payment_method => resolve(paymentMethodInformations.value = { three_d_secure: { payment_method } }))
      .catch(e => {
        sentry.error(new Error('#001502 - Create Stripe 3D Secure token failed'))

        reject(e)
      })
  })

  const setRecaptchaToken = async () => {
    if (!isRecaptchaRequired.value) return

    try {
      const token = await executeReCaptcha('login')

      _set(toValue(procedure_execution), 'actions_data.recaptcha.recaptcha_response', token)
    } catch (e) {
      throw e
    }
  }

  const setSecureToken = () => _set(toValue(procedure_execution), 'token', secureToken.value)

  const setSecurityTokens = () => Promise.all([set3dSecureToken(), setRecaptchaToken(), setSecureToken()])
    .catch(e => {
      throw e?.message || 'errors.an_error_occurred'
    })
    .finally(() => clearElements())

  return {
    secureToken,
    isThirdPartySecurityProcessing,
    isRecaptchaRequired,
    setSecurityTokens
  }
}